import React from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { TOURIST_PLACES } from "../lib/api";

export default function TouristGallery() {
  return (
    <section
      id="destinations"
      className="relative bg-black py-16 sm:py-24 lg:py-32 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5 mb-10 sm:mb-14">
          <div>
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#FFC300] font-bold">
              03 · Destinations
            </span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-6xl font-black uppercase tracking-tighter leading-none">
              Barcelona's<br />
              <span className="text-[#FFC300]">Iconic Stops</span>
            </h2>
          </div>
          <p className="text-neutral-400 max-w-md text-sm leading-relaxed">
            We know every corner of the city — from Gaudí masterpieces to coastal escapes.
            Tell us where you're heading.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
          {TOURIST_PLACES.map((p, idx) => (
            <motion.article
              key={p.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: (idx % 3) * 0.08 }}
              data-testid={`destination-card-${idx}`}
              className="group bg-black p-5 sm:p-6 lg:p-8 hover:bg-[#0A0A0A] transition-colors"
            >
              <div className="aspect-[4/3] bg-[#0A0A0A] overflow-hidden mb-6 border border-[#1a1a1a]">
                <img
                  src={p.img}
                  alt={p.name}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>

              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#FFC300] font-bold">
                  Barcelona
                </span>
              </div>
              <div className="flex items-center gap-0.5 mb-3" aria-label={`${p.rating} star rating`}>
                {[0, 1, 2, 3, 4].map((i) => {
                  const filled = p.rating >= i + 1;
                  const half = !filled && p.rating > i && p.rating < i + 1;
                  return (
                    <span key={i} className="relative inline-block w-3 h-3">
                      <Star className="absolute inset-0 w-3 h-3 text-neutral-700" />
                      {(filled || half) && (
                        <span
                          className="absolute inset-0 overflow-hidden"
                          style={{ width: filled ? "100%" : `${(p.rating - i) * 100}%` }}
                        >
                          <Star className="w-3 h-3 fill-[#FFC300] text-[#FFC300]" />
                        </span>
                      )}
                    </span>
                  );
                })}
                <span className="ml-1.5 text-[10px] text-neutral-400 font-semibold">{p.rating.toFixed(1)}</span>
              </div>
              <h3 className="font-display text-2xl uppercase font-black tracking-tight text-white">
                {p.name}
              </h3>
              <p className="mt-2 text-sm text-neutral-400 leading-relaxed">{p.desc}</p>

              <a
                href="#book"
                className="mt-6 inline-block text-[10px] uppercase tracking-[0.3em] font-bold text-white border-b border-[#FFC300] pb-1 hover:text-[#FFC300] transition-colors"
                data-testid={`book-to-${idx}-btn`}
              >
                Book Ride Here →
              </a>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
